'use client';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useEnterAnimation, useExitAnimation as useReactAriaExitAnimation } from '@react-aria/utils';
import { FocusScope } from 'react-aria';
import { cx } from '../../../utils/cx';
import { usePrevious } from '../../../utils/usePrevious';
import { ToolbarLayout, type ToolbarLayoutProps, type ToolbarLayoutDividerProps } from '../toolbarLayout';

const SEMANTIC_NAME = 'RomaEditorToolbar';
const SEMANTIC_NAME_SHELL = 'RomaEditorToolbarShell';
const SEMANTIC_NAME_LIST = 'RomaEditorToolbarList';
const SEMANTIC_NAME_DIVIDER = 'RomaEditorToolbarDivider';

export type EditorToolbarProps = React.HTMLAttributes<HTMLDivElement> & {
    /** Whether the toolbar should be visible. Keep the component mounted and drive this prop to allow exit animation. */
    isOpen: boolean;
    /** Whether to focus the first focusable child when the toolbar opens. */
    autoFocus?: boolean;
    /** Whether to restore focus when the toolbar finishes exiting. */
    restoreFocus?: boolean;
    /** Called after the toolbar finishes its exit animation. */
    onExitComplete?: () => void;
};
export type EditorToolbarShellProps = React.HTMLAttributes<HTMLDivElement>;
export type EditorToolbarListProps = ToolbarLayoutProps;

export type EditorToolbarDividerProps = ToolbarLayoutDividerProps;

export type UseEditorToolbarExitAnimationOptions = {
    /** Called before closing the toolbar, e.g. to close dependent popovers. */
    onExitStart?: () => void;
};

export type EditorToolbarExitAnimationState = {
    isOpen: boolean;
    startExit: (onExitComplete: () => void) => void;
    onExitComplete: () => void;
};

type EditorToolbarCompoundComponents = {
    Shell: typeof EditorToolbarShell;
    List: typeof EditorToolbarList;
    Divider: typeof EditorToolbarDivider;
    useExitAnimation: typeof useEditorToolbarExitAnimation;
};

/**
 * Coordinates parent teardown with EditorToolbar's built-in exit animation.
 */
const useEditorToolbarExitAnimation = ({
    onExitStart,
}: UseEditorToolbarExitAnimationOptions = {}): EditorToolbarExitAnimationState => {
    const [isOpen, setIsOpen] = useState(true);
    const exitCompleteRef = useRef<(() => void) | null>(null);

    const startExit = useCallback(
        (onExitComplete: () => void) => {
            onExitStart?.();
            exitCompleteRef.current = onExitComplete;
            setIsOpen(false);
        },
        [onExitStart],
    );

    const onExitComplete = useCallback(() => {
        const exitComplete = exitCompleteRef.current;
        exitCompleteRef.current = null;
        exitComplete?.();
    }, []);

    return { isOpen, onExitComplete, startExit };
};

const useEditorToolbarExitComplete = ({
    isExiting,
    isOpen,
    onExitComplete,
}: {
    isExiting: boolean;
    isOpen: boolean;
    onExitComplete?: () => void;
}) => {
    const wasOpen = usePrevious(isOpen, isOpen);
    const wasExiting = usePrevious(isExiting, isExiting);

    useEffect(() => {
        if (isOpen || isExiting || (!wasOpen && !wasExiting)) return;

        onExitComplete?.();
    }, [isExiting, isOpen, onExitComplete, wasExiting, wasOpen]);
};

const EditorToolbarAnimator: React.FC<{
    isExiting: boolean;
    contentRef: React.RefObject<HTMLDivElement>;
}> = ({ children, contentRef, isExiting }) => {
    const isEntering = useEnterAnimation(contentRef);

    return (
        <div
            ref={contentRef}
            className={cx(
                isEntering && !isExiting && 'duration-100 ease-out animate-in fade-in slide-in-from-bottom-4',
                isExiting && 'pointer-events-none duration-100 ease-in animate-out fade-out slide-out-to-bottom-4',
            )}
        >
            {children}
        </div>
    );
};

export const EditorToolbarRoot = forwardRef<HTMLDivElement, EditorToolbarProps>(
    ({ children, className, isOpen, autoFocus = true, restoreFocus = true, onExitComplete, ...props }, ref) => {
        const contentRef = useRef<HTMLDivElement>(null);
        const isExiting = useReactAriaExitAnimation(contentRef, isOpen);

        useEditorToolbarExitComplete({ isExiting, isOpen, onExitComplete });

        if (!isOpen && !isExiting) return null;

        return (
            <FocusScope autoFocus={autoFocus} restoreFocus={restoreFocus}>
                <div
                    {...props}
                    ref={ref}
                    aria-hidden={isExiting || props['aria-hidden']}
                    className={cx(SEMANTIC_NAME, 'fixed bottom-3xl left-1/2 z-50 -translate-x-1/2', className)}
                >
                    <EditorToolbarAnimator contentRef={contentRef} isExiting={isExiting}>
                        {children}
                    </EditorToolbarAnimator>
                </div>
            </FocusScope>
        );
    },
);

export const EditorToolbarShell = forwardRef<HTMLDivElement, EditorToolbarShellProps>(
    ({ children, className, ...props }, ref) => (
        <div
            {...props}
            ref={ref}
            className={cx(SEMANTIC_NAME_SHELL, 'rounded-xl bg-primary p-md shadow-md', className)}
        >
            {children}
        </div>
    ),
);

/**
 * Keeps the editor list API and semantic class on the shared layout.
 */
export const EditorToolbarList = forwardRef<HTMLDivElement, EditorToolbarListProps>(({ className, ...props }, ref) => (
    <ToolbarLayout {...props} ref={ref} className={cx(SEMANTIC_NAME_LIST, className)} />
));

/**
 * Keeps editor divider consumers compatible with the shared primitive.
 */
export const EditorToolbarDivider = forwardRef<HTMLDivElement, EditorToolbarDividerProps>(
    ({ className, ...props }, ref) => (
        <ToolbarLayout.Divider {...props} ref={ref} className={cx(SEMANTIC_NAME_DIVIDER, className)} />
    ),
);

EditorToolbarRoot.displayName = SEMANTIC_NAME;
EditorToolbarShell.displayName = SEMANTIC_NAME_SHELL;
EditorToolbarList.displayName = SEMANTIC_NAME_LIST;
EditorToolbarDivider.displayName = SEMANTIC_NAME_DIVIDER;

export const EditorToolbar = Object.assign(EditorToolbarRoot, {
    Shell: EditorToolbarShell,
    List: EditorToolbarList,
    Divider: EditorToolbarDivider,
    useExitAnimation: useEditorToolbarExitAnimation,
}) as typeof EditorToolbarRoot & EditorToolbarCompoundComponents;
